// React
import React, {Component} from 'react';

// Third party
import {inject, observer} from 'mobx-react';

// Bulma
import Button from 'components/tutorial/button';

// WS
import {Api} from '@wellstone-solutions/common';
import {Stack, Select, TextField} from '@wellstone-solutions/web';

import {
  fieldStatus,
  adminStructure,
  getAdminRole,
  genders,
  identities,
} from 'utils/Forms';
import {EventEmitter} from 'utils/EventEmitter';
import {Theme} from '@wellstone-solutions/common';

class EditAdminForm extends Component {
  state = {
    email: adminStructure.email.default,
    name: adminStructure.name.default,
    gender: adminStructure.gender.default.value,
    identity: adminStructure.identity.default.value,
    role: adminStructure.role.default,
    isProcessing: false,
  };

  componentDidMount() {
    this._loadUX();
  }

  _loadUX = async () => {
    let a = this.props.admin;
    a.role = getAdminRole(a.role);
    a.email = a.user.email;
    a.identity = a.identifies_as;
    this.setState(a);
  };

  _selectGender = (sel) => {
    this.setState({gender: sel});
  };

  _selectIdentity = (sel) => {
    this.setState({identity: sel});
  };

  _checkForErrors = () => {
    let hasErrors = false;
    for (let [key, value] of Object.entries(adminStructure)) {
      if (fieldStatus(this.state[key], value.test, value.required).error) {
        hasErrors = true;
      }
    }
    return hasErrors;
  };

  _updateAdmin = async () => {
    const org = this.props.meStore.me.membership.organization.id;

    const params = {
      gender: this.state.gender.toLowerCase(),
      identifies_as: this.state.identity.toLowerCase(),
      name: this.state.name,
      email: this.state.email,
    };

    try {
      this.setState({isProcessing: true});
      const promises = [
        Api.Instance.current().post(
          '/orgs/' + org + '/members/' + this.props.admin.id,
          params,
        ),
      ];

      // If email has changed, update the user email
      if (
        this.state.email &&
        this.state.email !== this.props.admin.user.email &&
        this.props.admin.user.id
      ) {
        promises.push(
          Api.Instance.current().put(`/users/${this.props.admin.user.id}`, {
            email: this.state.email,
          }),
        );
      }

      const responses = await Promise.all(promises);
      this.setState({isProcessing: false});
      EventEmitter.dispatch('closeModal', responses[0]);
    } catch (e) {
      this.setState({isProcessing: false});
    }
  };

  render() {
    const genderItems = genders.map(({name, value}) => ({label: name, value}));
    const isSuperAdmin = this.props.meStore.isSuperAdmin;
    const identityItems = identities.map(({name, value}) => ({
      label: name,
      value,
    }));

    return (
      <div style={styles.form} className={'edit-member-form'}>
        {/*NAME*/}
        <Stack
          direction={'column'}
          justifyContent="space-between"
          alignItems={'flex-start'}
          spacing={2}
          sx={{mt: 2, mb: 2}}>
          <TextField
            fullWidth
            placeholder="Full Name*"
            label="Full Name*"
            value={this.state.name}
            onChange={(e) => this.setState({name: e.target.value})}
            error={
              fieldStatus(
                this.state.name,
                adminStructure.name.test,
                adminStructure.name.required,
              ).error
            }
            helperText={
              fieldStatus(
                this.state.name,
                adminStructure.name.test,
                adminStructure.name.required,
              ).error
                ? 'This field is required'
                : ''
            }
            margin="normal"
            variant="outlined"
            size="small"
          />

          {/*EMAIL*/}
          <TextField
            fullWidth
            label="Email*"
            placeholder="Email"
            disabled={!isSuperAdmin}
            value={this.state.email}
            onChange={(e) => this.setState({email: e.target.value})}
            error={
              fieldStatus(
                this.state.email,
                adminStructure.email.test,
                adminStructure.email.required,
              ).error
            }
            helperText={
              fieldStatus(
                this.state.email,
                adminStructure.email.test,
                adminStructure.email.required,
              ).error
                ? fieldStatus(
                    this.state.email,
                    adminStructure.email.test,
                    adminStructure.email.required,
                  ).error === 'Field is Required'
                  ? 'This field is required'
                  : 'Invalid Email'
                : ''
            }
            margin="normal"
            variant="outlined"
            size="small"
          />

          {/* Role */}
          <TextField
            fullWidth
            label="Role"
            value={this.state?.role?.name}
            disabled
            size="small"
          />

          {/*SEX*/}
          <Select
            label="Sex"
            value={this.state.gender}
            items={genderItems}
            onChange={(e) => this._selectGender(e.target.value)}
            canUnselect={false}
            containerProps={{
              size: 'small',
              sx: {width: '100%', mb: 2},
            }}
          />

          {/*IDENTIFIES AS*/}
          <Select
            label="Identifies As"
            value={this.state.identity}
            items={identityItems}
            onChange={(e) => this._selectIdentity(e.target.value)}
            canUnselect={false}
            containerProps={{
              size: 'small',
              sx: {width: '100%', marginBottom: 2},
            }}
          />

          {/* BIRTH DATE */}
          <TextField
            fullWidth
            label="Birthdate"
            value={this.props.admin.birthdate || 'Birthdate not set'}
            disabled
            size="small"
          />
        </Stack>

        <Stack
          direction="row"
          justifyContent="flex-end"
          sx={{width: '100%', mt: 2}}>
          <Button
            style={{border: 'none', color: Theme.colorPalette.primary}}
            color="none"
            onClick={() => EventEmitter.dispatch('closeModal', null)}>
            Cancel
          </Button>
          <Button
            color="primary"
            disabled={this._checkForErrors() ? true : false}
            isprocessing={this.state.isProcessing}
            onClick={this._updateAdmin}>
            Save
          </Button>
        </Stack>
      </div>
    );
  }
}

const styles = {
  fieldLabel: {
    paddingRight: 20,
    whiteSpace: 'nowrap',
    fontWeight: 700,
    color: Theme.colorPalette.darkest,
  },
  form: {
    maxHeight: 'calc(100vh - 160px)',
    overflowY: 'auto',
    overflowX: 'hidden',
  },
  stackedRightItems: {
    alignItems: 'flex-end',
    justifyContent: 'flex-start',
    flexDirection: 'column',
  },
};

export default inject('meStore')(observer(EditAdminForm));
